import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import { completeVerificationStep } from '@/apis/experience/verification-setup';

import type { VerificationStepProps } from '../types';

import styles from './DeviceRegistrationStep.module.scss';

const DeviceRegistrationStep = ({ step, onStepComplete, progress }: VerificationStepProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationData, setRegistrationData] = useState<Record<string, unknown> | null>(null);

  const asyncCompleteStep = useApi(completeVerificationStep);

  // Check if WebAuthn is supported
  const isWebAuthnSupported = typeof window !== 'undefined' && 
    'navigator' in window && 
    'credentials' in navigator;

  // Start device registration process
  const handleStartRegistration = useCallback(async () => {
    if (!isWebAuthnSupported) {
      await handleError(new Error(t('mfa_setup.webauthn_not_supported')));
      return;
    }

    try {
      setIsSubmitting(true);
      
      // TODO: Integrate with existing WebAuthn registration flow
      // This would typically involve:
      // 1. Create WebAuthn registration options from backend
      // 2. Call navigator.credentials.create()
      // 3. Send response back to backend for verification
      
      // For now, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockRegistrationData = {
        id: 'device_registration_' + Date.now(),
        type: 'public-key',
        response: {
          clientDataJSON: 'mock_client_data',
          attestationObject: 'mock_attestation',
        },
      };

      setRegistrationData(mockRegistrationData);
      
      const [error, result] = await asyncCompleteStep('device', {
        deviceData: mockRegistrationData,
        metadata: {
          deviceName: navigator.userAgent,
          registeredAt: new Date().toISOString(),
        },
      });

      if (error) {
        await handleError(error);
        return;
      }

      if (result?.success) {
        await onStepComplete('device');
      }
    } catch (error) {
      await handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isWebAuthnSupported, asyncCompleteStep, handleError, onStepComplete, t]);

  if (step.status === 'completed') {
    return (
      <div className={styles.completedStep}>
        <div className={styles.completedIcon}>✅</div>
        <div className={styles.completedContent}>
          <h3>{t('mfa_setup.device_registration_completed')}</h3>
          <p>{t('mfa_setup.device_registration_completed_description')}</p>
          {progress?.deviceRegisteredAt && (
            <div className={styles.completedTime}>
              {t('mfa_setup.completed_at', { 
                time: new Date(progress.deviceRegisteredAt).toLocaleString() 
              })}
            </div>
          )}
          <div className={styles.deviceInfo}>
            <span className={styles.deviceIcon}>📱</span>
            <span>{t('mfa_setup.this_device_registered')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (step.status === 'pending') {
    return (
      <div className={styles.pendingStep}>
        <div className={styles.pendingIcon}>⏳</div>
        <div className={styles.pendingContent}>
          <h3>{t('mfa_setup.device_registration_pending')}</h3>
          <p>{t('mfa_setup.complete_previous_steps_first')}</p>
        </div>
      </div>
    );
  }

  if (!isWebAuthnSupported) {
    return (
      <div className={styles.unsupportedStep}>
        <div className={styles.unsupportedIcon}>⚠️</div>
        <div className={styles.unsupportedContent}>
          <h3>{t('mfa_setup.device_registration_unsupported')}</h3>
          <p>{t('mfa_setup.webauthn_not_available')}</p>
          <div className={styles.alternatives}>
            <p>{t('mfa_setup.try_different_browser')}</p>
            <ul>
              <li>{t('mfa_setup.chrome_recommendation')}</li>
              <li>{t('mfa_setup.firefox_recommendation')}</li>
              <li>{t('mfa_setup.safari_recommendation')}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>{step.icon}</div>
        <div>
          <h2 className={styles.stepTitle}>{step.title}</h2>
          <p className={styles.stepDescription}>{step.description}</p>
        </div>
      </div>

      <div className={styles.stepContent}>
        <div className={styles.registrationSection}>
          <div className={styles.devicePreview}>
            <div className={styles.deviceIcon}>📱</div>
            <div className={styles.deviceInfo}>
              <div className={styles.deviceName}>
                {t('mfa_setup.current_device')}
              </div>
              <div className={styles.deviceDetails}>
                {navigator.platform} • {t('mfa_setup.biometric_or_pin')}
              </div>
            </div>
          </div>

          <div className={styles.registrationInfo}>
            <h4>{t('mfa_setup.what_device_registration_does')}</h4>
            <ul className={styles.benefitsList}>
              <li>{t('mfa_setup.device_benefit_security')}</li>
              <li>{t('mfa_setup.device_benefit_convenience')}</li>
              <li>{t('mfa_setup.device_benefit_biometric')}</li>
            </ul>
          </div>

          <div className={styles.actionSection}>
            <Button
              title="mfa_setup.register_this_device"
              type="primary"
              size="large"
              isLoading={isSubmitting}
              onClick={handleStartRegistration}
            />
            
            <p className={styles.securityNote}>
              {t('mfa_setup.device_registration_security_note')}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.stepFooter}>
        <div className={styles.helpText}>
          {t('mfa_setup.device_registration_help')}
        </div>
      </div>
    </div>
  );
};

export default DeviceRegistrationStep;